import {
    getApiErrorMessage,
    IApiAxiosError,
    ILibraryListComparison,
    updateLibraryComparison,
} from "@/api";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface RenameComparisonDialogProps {
    open: boolean;
    onClose: () => void;
    comparison: ILibraryListComparison;
    onRenamed: () => void;
}

export const RenameComparisonDialog = ({
    open,
    onClose,
    comparison,
    onRenamed,
}: RenameComparisonDialogProps) => {
    const [name, setName] = useState(comparison.name);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setName(comparison.name);
    }, [comparison.name]);

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            updateLibraryComparison(comparison.id, { name: name.trim() }),
        onSuccess: () => {
            toast.success("Comparison renamed successfully");
            onRenamed();
        },
        onError: (err: IApiAxiosError) => {
            setError(getApiErrorMessage(err, "Failed to rename comparison"));
        },
    });

    const handleSubmit = () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError("Name cannot be empty");
            return;
        }
        if (trimmed === comparison.name) {
            onClose();
            return;
        }
        setError(null);
        mutate();
    };

    const handleClose = () => {
        setName(comparison.name);
        setError(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Rename Comparison</DialogTitle>
                </DialogHeader>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                    className="space-y-3"
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="rename-comparison-name">Name</Label>
                        <Input
                            id="rename-comparison-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onFocus={(e) => e.currentTarget.select()}
                        />
                    </div>
                    {error && (
                        <p className="text-destructive text-sm">{error}</p>
                    )}
                    <DialogFooter>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={handleClose}
                            disabled={isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={isPending || !name}>
                            {isPending ? (
                                <>
                                    <Loader2 className="mr-2 size-4 animate-spin" />
                                    Saving…
                                </>
                            ) : (
                                "Save"
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
